import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { GroupService } from '../../../services/group.service';
import { ProjectService } from '../../../services/project.service';
import { ProfileService } from '../../../services/profile.service';
import { Group } from '../../../dtos/group';
import { UserInfo } from '../../../dtos/user-info';
import { Location } from '@angular/common';

@Component({
  selector: 'app-group-manage',
  templateUrl: './group.component.html',
  styleUrls: ['./group.component.scss'],
  standalone: false,
})
export class GroupComponent implements OnInit, OnDestroy {
  readonly group = signal<Group | null>(null);
  readonly userInfo = signal<UserInfo | null>(null);
  readonly errorMessage = signal('');
  readonly copiedInviteGroupId = signal<string | null>(null);
  readonly showGroupEditModal = signal(false);
  readonly showLeaveConfirm = signal(false);
  readonly leaveLoading = signal(false);
  readonly leaveError = signal('');
  readonly activeTab = signal('overview');

  groupId = '';
  private sub?: Subscription;
  private navigationSub?: Subscription;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private groupService: GroupService,
    private projectService: ProjectService,
    private location: Location,
    private profileService: ProfileService,
  ) {}

  ngOnInit(): void {
    this.sub = this.route.params.subscribe((params) => {
      this.groupId = params['id'];
      if (!this.groupId) {
        this.router.navigate(['/groups']);
        return;
      }
      this.loadGroup();
    });
    this.loadUserInfo();

    this.syncActiveTab();
    this.navigationSub = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncActiveTab());
  }

  private syncActiveTab(): void {
    const child = this.route.snapshot.firstChild;
    const path = child?.url[0]?.path;
    this.activeTab.set(path || 'overview');
  }

  ngOnDestroy(): void {
    this.sub?.unsubscribe();
    this.navigationSub?.unsubscribe();
  }

  private loadGroup(): void {
    this.errorMessage.set('');
    this.groupService.getGroupById(this.groupId).subscribe({
      next: (group) => this.group.set(group),
      error: (err) => {
        console.error('Error loading group info', err);
        this.errorMessage.set('Could not load group information.');
      },
    });
  }

  private loadUserInfo(): void {
    this.profileService.getCurrentUser().subscribe({
      next: (user) => this.userInfo.set(user),
      error: () => this.userInfo.set(null),
    });
  }

  isGroupOwner(): boolean {
    const g = this.group();
    const u = this.userInfo();
    return !!g && !!u?.id && String(g.ownerUserId) === String(u.id);
  }

  get leaveMessage(): string {
    const name = this.group()?.name ?? '';
    return `Are you sure you want to leave the group "${name}"? This action cannot be undone.`;
  }

  askLeaveGroup(): void {
    this.leaveError.set('');
    this.showLeaveConfirm.set(true);
  }

  confirmLeaveGroup(): void {
    const g = this.group();
    if (!g || this.leaveLoading()) return;

    this.leaveLoading.set(true);
    this.leaveError.set('');

    this.groupService.leaveGroup(g.id).subscribe({
      next: () => {
        this.showLeaveConfirm.set(false);
        this.leaveLoading.set(false);
        this.router.navigate(['/groups']);
      },
      error: (err) => {
        this.leaveError.set(
          err?.error?.message ??
            err?.error?.detail ??
            err?.message ??
            'An error occurred while leaving the group.',
        );
        this.showLeaveConfirm.set(false);
        this.leaveLoading.set(false);
      },
    });
  }

  cancelLeaveGroup(): void {
    this.showLeaveConfirm.set(false);
    this.leaveLoading.set(false);
    this.leaveError.set('');
  }

  doGoBack(): void {
    this.router.navigate(['/groups']);
  }

  buildInviteLink(group: Group): string {
    const tree = this.router.createUrlTree(['/join-group'], {
      queryParams: { inviteToken: group.inviteToken },
    });
    return `${window.location.origin}${this.location.prepareExternalUrl(this.router.serializeUrl(tree))}`;
  }

  async copyInviteLink(): Promise<void> {
    const g = this.group();
    if (!g || !g.inviteActive || !g.inviteToken) return;

    const inviteLink = this.buildInviteLink(g);

    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(inviteLink);
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = inviteLink;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }

      this.copiedInviteGroupId.set(g.id);
      window.setTimeout(() => {
        if (this.copiedInviteGroupId() === g.id) {
          this.copiedInviteGroupId.set(null);
        }
      }, 2000);
    } catch (error) {
      console.error('Could not copy invite link', error);
    }
  }

  openGroupEdit(): void {
    this.showGroupEditModal.set(true);
  }

  closeGroupEdit(): void {
    this.showGroupEditModal.set(false);
  }

  saveGroup(updatedGroup: Group): void {
    this.group.set(updatedGroup);
    this.closeGroupEdit();
  }

  deleteGroup(): void {
    this.router.navigate(['/groups']);
  }

  switchTab(tab: string): void {
    this.activeTab.set(tab);
    if (tab === 'overview') {
      this.router.navigate(['/groups', this.groupId]);
    } else {
      this.router.navigate(['/groups', this.groupId, tab]);
    }
  }
}
